import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../utils/echarts'
import type { TFunction } from 'i18next'
import type { ElectricityGridOperator } from '@popety-io/types'
import { Icon } from '../../../atoms/Icon'

export interface PriceEvolutionChartRef {
  exportChart: () => string | null
}

interface PriceEvolutionChartProps {
  gridOperators: ElectricityGridOperator[]
  height?: number
  t: TFunction
}

// Color palette for grid operator lines
const GRID_OPERATOR_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#f97316',
  '#ec4899',
]

export const PriceEvolutionChart = forwardRef<
  PriceEvolutionChartRef,
  PriceEvolutionChartProps
>(({ gridOperators, height = 350, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  // Filter grid operators with yearly data
  const operatorsWithHistory = gridOperators.filter(
    (op) => op.yearly_data && op.yearly_data.length > 0
  )

  useEffect(() => {
    if (
      !chartRef.current ||
      !operatorsWithHistory ||
      operatorsWithHistory.length === 0
    )
      return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Collect all years across all operators
    const allYears = new Set<number>()
    operatorsWithHistory.forEach((op) => {
      op.yearly_data?.forEach((yd) => allYears.add(yd.year))
    })
    const sortedYears = Array.from(allYears).sort((a, b) => a - b)

    // Build series for each operator
    const series: echarts.SeriesOption[] = operatorsWithHistory.map(
      (op, index) => {
        const color = GRID_OPERATOR_COLORS[index % GRID_OPERATOR_COLORS.length]
        const dataMap = new Map(
          op.yearly_data?.map((yd) => [yd.year, yd.prices.price_total]) || []
        )

        return {
          name: op.grid_operator_name,
          type: 'line',
          data: sortedYears.map((year) => dataMap.get(year) ?? null),
          lineStyle: { color, width: 2 },
          itemStyle: { color },
          symbol: 'circle',
          symbolSize: 5,
          smooth: true,
          connectNulls: true,
        }
      }
    )

    const legendData = series.map((s) => s.name as string)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const year = params[0].axisValue
          let content = `<strong>${year}</strong><br/>`
          params.forEach((p: any) => {
            if (p.value !== null && p.value !== undefined) {
              content += `<span style="color:${p.color}">●</span> ${p.seriesName}: ${Number(p.value).toFixed(2)} ${t('gridOperators.unit')}<br/>`
            }
          })
          return content
        },
      },
      legend: {
        data: legendData,
        top: 10,
        textStyle: { fontSize: 12 },
        type: 'scroll',
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: sortedYears.map(String),
        axisLabel: { fontSize: 11, color: '#1f2937' },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
      },
      yAxis: {
        type: 'value',
        name: t('charts.priceEvolution.axis.price'),
        axisLabel: {
          fontSize: 11,
          color: '#1f2937',
          formatter: '{value}',
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
      },
      series,
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [operatorsWithHistory, height, t])

  if (!operatorsWithHistory || operatorsWithHistory.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.noData')}
      </div>
    )
  }

  // Calculate insights from first operator as representative
  const firstOp = operatorsWithHistory[0]
  const sortedData = [...(firstOp.yearly_data || [])].sort(
    (a, b) => a.year - b.year
  )
  const firstYear = sortedData[0]
  const lastYear = sortedData[sortedData.length - 1]

  let avgChange = 0
  if (firstYear && lastYear && firstYear.prices.price_total > 0) {
    avgChange =
      ((lastYear.prices.price_total - firstYear.prices.price_total) /
        firstYear.prices.price_total) *
      100
  }

  return (
    <div>
      <div
        ref={chartRef}
        style={{ width: '100%', height: `${height}px`, minHeight: '300px' }}
      />

      {/* Trend insights */}
      {firstYear && lastYear && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Icon name="trending-up" size="xs" />{' '}
            {t('charts.priceEvolution.summary.title', {
              startYear: firstYear.year,
              endYear: lastYear.year,
            })}
          </div>
          <div
            style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.4' }}
          >
            • <strong>{t('charts.priceEvolution.summary.avgChange')}:</strong>{' '}
            {avgChange >= 0 ? '+' : ''}
            {avgChange.toFixed(1)}%
            <br />• <strong>
              {t('charts.priceEvolution.summary.trend')}:
            </strong>{' '}
            {avgChange < -2
              ? t('charts.priceEvolution.summary.decreasing')
              : avgChange > 2
                ? t('charts.priceEvolution.summary.increasing')
                : t('charts.priceEvolution.summary.stable')}
          </div>
        </div>
      )}
    </div>
  )
})

PriceEvolutionChart.displayName = 'PriceEvolutionChart'
